<script lang="ts" setup>
import type { ContentFeatured } from '~/models/Content/ContentFeatured'
import type { BaseItem } from '~/models/Content/BaseItem'
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { faArrowRightLong } from '@fortawesome/duotone-regular-svg-icons'
import { gradeTypeNames } from '~/utils/gradeSorter'
import { PendoTrackName } from '~/models/Pendo'
import { GradeType } from '~/models/GradeType'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import usePendo from '~/composables/usePendo'
import { useLottie } from '~/composables/useLottie'
import useContentApi from '~/api/contentApi'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import LinkOrAnchor from '~/components/utils/LinkOrAnchor.vue'

const { featured, item } = defineProps<{
  featured?: ContentFeatured
  item?: BaseItem
  hasTasks?: boolean
}>()

const { buildResourceUrl } = useUrlBuilder()
const { findContents } = useContentApi()
const { lottieJSON, getLottie } = useLottie()
const { pendoTrack, pendoFlushNow } = usePendo()

const autoplay = !matchMedia('(prefers-reduced-motion)').matches
const loadLottie = (content: ContentFeatured) => {
  if (Number(content.lottie?.destinationContentId) === 0)
    return null
  return getLottie(Number(content.lottie?.destinationContentId))
}

const { data: lottie } = useQuery({
  enabled: computed(() => featured && Number(featured.lottie?.destinationContentId) > 0),
  staleTime: Infinity,
  queryKey: ['lower-primary-featured-lottie', 'dont-invalidate-on-grade-change'],
  queryFn: async () => {
    featured && await loadLottie(featured)
    return { json: lottieJSON.value }
  }
})

const { data: relation } = useQuery({
  enabled: computed(() => featured && Number(featured.relation?.destinationContentId) > 0),
  staleTime: Infinity,
  queryKey: ['lower-primary-featured-relation', 'dont-invalidate-on-grade-change'],
  queryFn: async () => findContents<BaseItem>({
    contentIdCriterion: [Number(featured!.relation!.destinationContentId)],
    mainLocationCriterion: true,
  }, 1).then(([data]) => data)
})

const relationUrl = computed(() => relation?.value ? buildResourceUrl(relation.value) : '')
const itemUrl = computed(() => item ? buildResourceUrl(item) : '')

const track = () => {
  if (!relation?.value)
    return
  pendoTrack(PendoTrackName.BannerArticle, {
    title: relation.value.title,
    gradeType: gradeTypeNames[GradeType.Grade1To4],
    url: relationUrl.value || '',
  })
  pendoFlushNow()
}
</script>

<template>
  <div class="flex w-full flex-col items-center max-lg:mt-12 lg:flex-row" :class="{ 'pt-30': hasTasks }">
    <div class="relative mx-auto flex w-full justify-center pr-6">
      <div v-if="featured" class="bg-blue-pop text-blue-60 group pointer-events-auto z-10 flex h-max max-w-[350px] -rotate-6 flex-col gap-2 rounded-xl px-6 py-4 shadow transition-all duration-300 hover:rotate-0 has-focus-visible:ring">
        <span
          v-if="relation"
          class="text-sm font-semibold tracking-wider uppercase"
          v-text="relation?.title"
        />
        <span v-if="relation" class="font-calistoga fluid-text-lg !leading-8">
          <LinkOrAnchor
            :link="relationUrl"
            class="line-clamp-3 outline-0 before:absolute before:inset-0 before:rounded before:transition before:hover:shadow-xl before:focus-visible:ring"
            @click="track"
          >
            {{ featured.title }}
          </LinkOrAnchor>
        </span>
        <RichTextRenderer
          :text="featured.description"
          class="text-gray-50"
        />
        <font-awesome-icon
          :icon="faArrowRightLong"
          class="transition-all duration-500 group-hover:ml-6"
          size="xl"
        />
      </div>
      <div v-if="item" class="bg-yellow-30 group text-green-60 font-calistoga pointer-events-auto z-20 mt-22 -ml-6 max-w-[300px] rotate-6 rounded-xl px-6 py-4 text-lg transition-all duration-300 hover:rotate-0 has-focus-visible:ring">
        <span v-if="relation" class="font-calistoga text-lg">
          <LinkOrAnchor
            :link="itemUrl"
            class="line-clamp-3 outline-0 before:absolute before:inset-0 before:rounded before:transition before:hover:shadow-xl before:focus-visible:ring"
            @click="track"
          >
            {{ item.title }}
          </LinkOrAnchor>
        </span>
        <font-awesome-icon
          :icon="faArrowRightLong"
          class="transition-all duration-500 group-hover:ml-3"
        />
      </div>
    </div>
    <LottieAnimation
      v-if="lottie?.json"
      :animation-data="lottie?.json"
      :auto-play="autoplay"
      :speed="1.2"
      class="!size-full sm:!w-[70%] md:!size-[500px] lg:absolute lg:right-0 lg:bottom-[240px] lg:max-w-[50dvw]"
      :class="{ 'lg:!bottom-12': hasTasks }"
    />
  </div>
</template>
